import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { campaignService, CampaignFilters, CreateCampaignRequest, UpdateCampaignRequest } from '@services/campaign.service'
import { toast } from 'react-hot-toast'

// Helper to extract error message from axios error
function getErrorMessage(error: any): string {
  return error?.response?.data?.error || error?.response?.data?.message || error?.message || 'An error occurred'
}

export function useCampaigns(filters: CampaignFilters = {}) {
  return useQuery({
    queryKey: ['campaigns', filters],
    queryFn: () => campaignService.fetchCampaigns(filters),
  })
}

export function useCampaign(id: number) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: () => campaignService.getCampaign(id),
    enabled: !!id,
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCampaignRequest) => campaignService.createCampaign(data),
    onSuccess: () => {
      toast.success('Campaign created successfully')
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
    onError: (error: any) => {
      console.error('Failed to create campaign:', error)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateCampaign(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateCampaignRequest) => campaignService.updateCampaign(id, data),
    onSuccess: () => {
      toast.success('Campaign updated successfully')
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['campaign', id] })
    },
    onError: (error: any) => {
      console.error('Failed to update campaign:', error)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => campaignService.deleteCampaign(id),
    onSuccess: () => {
      toast.success('Campaign deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
    onError: (error: any) => {
      console.error('Failed to delete campaign:', error)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useStartCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => campaignService.startCampaign(id),
    onSuccess: async () => {
      toast.success('Campaign started!')
      // Očisti keš i forsiraj refetch
      await queryClient.resetQueries({ queryKey: ['campaigns'] })
      await queryClient.refetchQueries({ queryKey: ['campaigns'], type: 'all' })
    },
    onError: (error: any) => {
      console.error('Failed to start campaign:', error)
      toast.error(getErrorMessage(error))
    },
  })
}

export function usePauseCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => campaignService.pauseCampaign(id),
    onSuccess: async () => {
      toast.success('Campaign paused!')
      // Očisti keš i forsiraj refetch
      await queryClient.resetQueries({ queryKey: ['campaigns'] })
      await queryClient.refetchQueries({ queryKey: ['campaigns'], type: 'all' })
    },
    onError: (error: any) => {
      console.error('Failed to pause campaign:', error)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useResumeCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => campaignService.resumeCampaign(id),
    onSuccess: async () => {
      toast.success('Campaign resumed!')
      // Očisti keš i forsiraj refetch
      await queryClient.resetQueries({ queryKey: ['campaigns'] })
      await queryClient.refetchQueries({ queryKey: ['campaigns'], type: 'all' })
    },
    onError: (error: any) => {
      console.error('Failed to resume campaign:', error)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useStopCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => campaignService.stopCampaign(id),
    onSuccess: async () => {
      toast.success('Campaign stopped successfully')
      // Očisti keš i forsiraj refetch
      await queryClient.resetQueries({ queryKey: ['campaigns'] })
      await queryClient.refetchQueries({ queryKey: ['campaigns'], type: 'all' })
    },
    onError: (error: any) => {
      console.error('Failed to stop campaign:', error)
      toast.error(getErrorMessage(error))
    },
  })
}

export function useAddClientsToCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ campaignId, clientIds }: { campaignId: number; clientIds: number[] }) =>
      campaignService.addClientsToCampaign(campaignId, clientIds),
    onSuccess: (_, { campaignId }) => {
      toast.success('Clients added to campaign successfully')
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
    onError: (error: any) => {
      console.error('Failed to add clients to campaign:', error)
      toast.error(getErrorMessage(error))
    },
  })
}
